import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { preparePrintHtml } from '../../shared/print-html';
import { SalesApiService, UpdateSalesDocumentProfilePrintTemplatePayload } from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-document-profile-print',
  imports: [FormsModule, ButtonModule, DialogModule, InputTextModule, InfoHintComponent],
  template: `
    <ain-info-hint
      [titleKey]="'sales.documentProfiles.print.hint.title'"
      [descriptionKey]="'sales.documentProfiles.print.hint.description'"
      [bulletKeys]="[
        'sales.documentProfiles.print.hint.bullets.placeholders',
        'sales.documentProfiles.print.hint.bullets.samplePdf',
        'sales.documentProfiles.print.hint.bullets.pdfSave'
      ]" />

    <section class="card">
      <div class="toolbar">
        <button pButton type="button" severity="secondary" [label]="t('sales.documentProfiles.print.loadDefault')" (click)="loadDefaultTemplate()" [disabled]="loading() || saving()"></button>
        <button pButton type="button" severity="secondary" [label]="t('sales.documentProfiles.print.preview')" (click)="preview()" [disabled]="loading() || saving()"></button>
        <button pButton type="button" [label]="t('common.save')" (click)="save()" [disabled]="loading() || saving()"></button>
      </div>

      @if (showUsesDefaultBadge()) {
        <p class="badge">{{ t('sales.documentProfiles.print.usesDefault') }}</p>
      }

      <label class="wide">
        <span>{{ t('sales.documentProfiles.print.templateHtml') }}</span>
        <textarea pInputText rows="18" [(ngModel)]="printTemplateHtml"></textarea>
      </label>

      <label class="wide">
        <span>{{ t('sales.documentProfiles.print.layoutJson') }}</span>
        <textarea pInputText rows="6" [(ngModel)]="printLayoutJson" [placeholder]="t('sales.documentProfiles.print.layoutJsonPlaceholder')"></textarea>
      </label>

      @if (errorMessage()) {
        <p class="error">{{ errorMessage() }}</p>
      }
    </section>

    <p-dialog [header]="t('sales.documentProfiles.print.previewTitle')" [(visible)]="previewOpen" [modal]="true" [style]="{ width: '920px', maxWidth: '96vw' }">
      @if (previewHtml()) {
        <iframe class="preview-frame" [srcdoc]="previewHtml()"></iframe>
      }
    </p-dialog>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; margin-top: 10px; }
    .toolbar { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
    label.wide { display: grid; gap: 6px; margin-top: 10px; }
    textarea { width: 100%; font-family: Consolas, monospace; font-size: 12px; }
    .badge { color: #1d4ed8; font-size: 13px; margin: 0 0 8px; }
    .error { color: #b91c1c; }
    .preview-frame { width: 100%; min-height: 70vh; border: 1px solid #e5e7eb; border-radius: 8px; }
  `]
})
export class SalesDocumentProfilePrintComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly localization = inject(LocalizationService);

  @Input({ required: true }) profileId!: string;

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly usesDefaultTemplate = signal(true);
  readonly errorMessage = signal('');
  readonly previewOpen = signal(false);
  readonly previewHtml = signal('');

  printTemplateHtml = '';
  printLayoutJson = '';
  private defaultPrintTemplateHtml = '';

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.errorMessage.set('');
    this.api.getDocumentProfilePrintTemplate(this.profileId).subscribe({
      next: row => {
        this.printTemplateHtml = row.printTemplateHtml ?? '';
        this.printLayoutJson = row.printLayoutJson ?? '';
        this.defaultPrintTemplateHtml = row.defaultPrintTemplateHtml ?? '';
        this.usesDefaultTemplate.set(row.usesDefaultTemplate);
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('sales.documentProfiles.print.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  save(): void {
    this.saving.set(true);
    this.errorMessage.set('');
    const payload: UpdateSalesDocumentProfilePrintTemplatePayload = {
      printTemplateHtml: this.printTemplateHtml.trim() || null,
      printLayoutJson: this.printLayoutJson.trim() || null
    };
    this.api.updateDocumentProfilePrintTemplate(this.profileId, payload).subscribe({
      next: row => {
        this.usesDefaultTemplate.set(row.usesDefaultTemplate);
        this.saving.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('sales.documentProfiles.print.errorSave'));
        this.saving.set(false);
      }
    });
  }

  preview(): void {
    this.errorMessage.set('');
    this.api.previewDocumentProfilePrintTemplate(this.profileId).subscribe({
      next: row => {
        this.previewHtml.set(preparePrintHtml(row.html));
        this.previewOpen.set(true);
      },
      error: () => this.errorMessage.set(this.t('sales.documentProfiles.print.errorPreview'))
    });
  }

  loadDefaultTemplate(): void {
    if (!this.defaultPrintTemplateHtml) {
      this.errorMessage.set(this.t('sales.documentProfiles.print.errorLoadDefault'));
      return;
    }

    this.errorMessage.set('');
    this.printTemplateHtml = this.defaultPrintTemplateHtml;
    this.usesDefaultTemplate.set(false);
  }

  showUsesDefaultBadge(): boolean {
    return this.usesDefaultTemplate() && !this.printTemplateHtml.trim();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
